import { Alert, Button, DatePicker, Pagination, Select, Spin } from 'antd';
import moment, { Moment } from 'moment';
import { Profile } from 'oidc-client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { filterOptionForSearch } from '../../helpers/normalizeForSearch';
import { CompetitionsClient, CompetitionVm, MatchVm, TeamsClient, TeamVm } from '../../utils/api';
import './insights.css';
import {
    canAccessInsights,
    canUseInsightsPages,
    insightsIsLiveStatus,
    insightsRoleLabel,
    insightsScoreText,
} from './insightsAccess';
import { clubLabel, parseClaimId, teamLabel, useInsightsClub } from './insightsClubScope';
import {
    ClubMatchView,
    ClubSummary,
    emptyClubSummary,
    isFinishedMatch,
    ourTeamIds,
    summarizeClubMatches,
    toClubMatchView,
} from './insightsClubSummary';
import InsightsClubSummaryPanel from './InsightsClubSummaryPanel';
import InsightsLastMatchPanel from './InsightsLastMatchPanel';
import InsightsLegend from './InsightsLegend';
import InsightsNav from './InsightsNav';
import { loadPlayerSeason, PlayerSeasonRow, shootingPercentText } from './insightsPlayerSeason';
import { fetchClubMatches } from './insightsQueries';
import InsightsTopbar from './InsightsTopbar';

interface Props {
    userProfile?: Profile;
}

interface MatchListFilters {
    competitionId?: number;
    opponentTeamId?: number;
    dateRange: [Moment, Moment] | null;
}

const PAGE_SIZE = 25;
// One flat request of recent fixtures feeds the season block; events are never loaded here.
const SUMMARY_SIZE = 100;
// Leaders do read events, so the home page keeps that sample deliberately small.
const LEADERS_MATCHES = 5;
const LEADERS_SHOWN = 5;

const playerHref = (playerId: number): string =>
    Routes.ROUTE_INSIGHTS_PLAYER.replace(ReplaceStrings.PLAYER_ID, playerId.toString());

const emptyFilters: MatchListFilters = {
    competitionId: undefined,
    opponentTeamId: undefined,
    dateRange: null,
};

const matchHref = (matchId: number): string =>
    Routes.ROUTE_INSIGHTS_MATCH.replace(ReplaceStrings.MATCH_ID, matchId.toString());

const hasActiveFilters = (filters: MatchListFilters): boolean =>
    filters.competitionId != null || filters.opponentTeamId != null || filters.dateRange != null;

const Insights: React.FC<Props> = ({ userProfile }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered Competitions / Teams / Matches/search in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);
    const federationOwnerId = parseClaimId(userProfile && userProfile['federationOwner']);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [matches, setMatches] = useState<MatchVm[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [loadingMatches, setLoadingMatches] = useState(false);
    const [matchesError, setMatchesError] = useState<string | undefined>();
    const [competitions, setCompetitions] = useState<CompetitionVm[]>([]);
    const [allTeams, setAllTeams] = useState<TeamVm[]>([]);
    const [filters, setFilters] = useState<MatchListFilters>(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState<MatchListFilters>(emptyFilters);
    const [summary, setSummary] = useState<ClubSummary>(emptyClubSummary);
    const [lastMatch, setLastMatch] = useState<ClubMatchView | undefined>();
    const [nextMatch, setNextMatch] = useState<MatchVm | undefined>();
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [leaders, setLeaders] = useState<PlayerSeasonRow[]>([]);
    const [leadersMatches, setLeadersMatches] = useState(0);
    const [leadersLoading, setLeadersLoading] = useState(false);

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    // Season block ignores the filter bar on purpose: it is the club's standing picture.
    useEffect(() => {
        let cancelled = false;

        const loadSummary = async (): Promise<void> => {
            if (
                !allowed ||
                !canUseInsightsPages(userRole) ||
                selectedClubId == null ||
                teamIds.length === 0
            ) {
                setSummary(emptyClubSummary);
                setLastMatch(undefined);
                setNextMatch(undefined);
                return;
            }

            setSummaryLoading(true);
            try {
                const result = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: SUMMARY_SIZE,
                });
                if (cancelled) return;

                const recent = result.pageItems || [];
                setSummary(summarizeClubMatches(recent, teamIds));

                const finished = recent.find(
                    (match: MatchVm) => isFinishedMatch(match) && toClubMatchView(match, teamIds)
                );
                setLastMatch(finished ? toClubMatchView(finished, teamIds) : undefined);

                // Newest first, so the closest upcoming fixture is the last unfinished one.
                const upcoming = recent.filter((match: MatchVm) => !isFinishedMatch(match));
                setNextMatch(upcoming.length ? upcoming[upcoming.length - 1] : undefined);
            } catch {
                if (!cancelled) {
                    setSummary(emptyClubSummary);
                    setLastMatch(undefined);
                    setNextMatch(undefined);
                }
            } finally {
                if (!cancelled) setSummaryLoading(false);
            }
        };

        loadSummary();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId, teamIds]);

    // Leaders read match events, so they load separately and never block the page.
    useEffect(() => {
        let cancelled = false;

        const loadLeaders = async (): Promise<void> => {
            if (
                !allowed ||
                !canUseInsightsPages(userRole) ||
                selectedClubId == null ||
                teamIds.length === 0
            ) {
                setLeaders([]);
                setLeadersMatches(0);
                return;
            }

            setLeadersLoading(true);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: LEADERS_MATCHES * 2,
                });
                if (cancelled) return;

                const finished = (page.pageItems || [])
                    .filter((match: MatchVm) => isFinishedMatch(match))
                    .slice(0, LEADERS_MATCHES);

                const season = await loadPlayerSeason(finished, teamIds);
                if (cancelled) return;

                setLeaders(season.rows.filter((row: PlayerSeasonRow) => row.goals > 0));
                setLeadersMatches(season.matchesAnalysed);
            } catch {
                if (!cancelled) {
                    setLeaders([]);
                    setLeadersMatches(0);
                }
            } finally {
                if (!cancelled) setLeadersLoading(false);
            }
        };

        loadLeaders();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId, teamIds]);

    useEffect(() => {
        let cancelled = false;

        const loadFilterOptions = async (): Promise<void> => {
            if (!allowed || !canUseInsightsPages(userRole)) return;
            try {
                const [comps, teamList] = await Promise.all([
                    new CompetitionsClient().getAll(),
                    new TeamsClient().getAll(federationOwnerId),
                ]);
                if (cancelled) return;
                setCompetitions(comps || []);
                setAllTeams(teamList || []);
            } catch {
                if (!cancelled) {
                    setCompetitions([]);
                    setAllTeams([]);
                }
            }
        };

        loadFilterOptions();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, federationOwnerId]);

    const competitionOptions = useMemo(
        () =>
            competitions
                .slice()
                .sort((a, b) =>
                    (a.nameUniversal || a.name || '').localeCompare(b.nameUniversal || b.name || '')
                )
                .map((competition: CompetitionVm) => ({
                    value: competition.id,
                    label: competition.nameUniversal || competition.name,
                })),
        [competitions]
    );

    // Opponents are every team that is not ours; our own side comes from the topbar team select.
    const opponentOptions = useMemo(
        () =>
            allTeams
                .filter((team: TeamVm) => selectedClubId == null || team.clubId !== selectedClubId)
                .sort((a: TeamVm, b: TeamVm) => teamLabel(a).localeCompare(teamLabel(b)))
                .map((team: TeamVm) => ({ value: team.id, label: teamLabel(team) })),
        [allTeams, selectedClubId]
    );

    const loadMatches = useCallback(
        async (page: number, filterState: MatchListFilters = appliedFilters): Promise<void> => {
            if (!allowed || !canUseInsightsPages(userRole) || selectedClubId == null) return;

            setLoadingMatches(true);
            setMatchesError(undefined);
            try {
                const result = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    opponentTeamId: filterState.opponentTeamId,
                    competitionId: filterState.competitionId,
                    dateRange: filterState.dateRange,
                    pageIndex: page,
                    pageSize: PAGE_SIZE,
                });
                setMatches(result.pageItems || []);
                setTotalItems(result.totalItems || 0);
                setPageIndex(result.currentPage || page);
            } catch {
                setMatches([]);
                setTotalItems(0);
                setMatchesError('Could not load matches. Try again in a moment.');
            } finally {
                setLoadingMatches(false);
            }
        },
        [allowed, userRole, appliedFilters, selectedClubId, selectedTeamId]
    );

    useEffect(() => {
        loadMatches(1, appliedFilters);
    }, [loadMatches, appliedFilters]);

    const applyFilters = (): void => {
        setMatchesError(undefined);
        setAppliedFilters({ ...filters });
    };

    const clearFilters = (): void => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
    };

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
                description="Open staging.arena.total-waterpolo.com or statistics.arena.total-waterpolo.com (or run locally with REACT_APP_STATISTICS_MODE=true)."
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>
                        Club Insights is for Federation and Club accounts that enter statistics. Ask
                        Total Waterpolo if you need access for your club.
                    </p>
                </div>
            </div>
        );
    }

    const email =
        (userProfile &&
            (userProfile.email || userProfile.preferred_username || userProfile.name)) ||
        '-';

    const filtersDirty =
        filters.competitionId !== appliedFilters.competitionId ||
        filters.opponentTeamId !== appliedFilters.opponentTeamId ||
        (filters.dateRange?.[0]?.valueOf() || 0) !==
            (appliedFilters.dateRange?.[0]?.valueOf() || 0) ||
        (filters.dateRange?.[1]?.valueOf() || 0) !==
            (appliedFilters.dateRange?.[1]?.valueOf() || 0);

    const clubName = clubLabel(scope.selectedClub);
    const teamName = scope.teams.find((team: TeamVm) => team.id === selectedTeamId);
    const scopeLine = [clubName || 'No club', teamName ? teamLabel(teamName) : 'All teams']
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope}>
                <div className="insightsScopeItem">
                    <strong>{insightsRoleLabel(userRole)}</strong>
                    <span title={email}>{email}</span>
                </div>
            </InsightsTopbar>

            <div className="insightsBody">
                <InsightsNav active="overview" />
                <InsightsLegend />

                <div className="insightsHero">
                    <div>
                        <h1>Insights</h1>
                        <p>
                            Everything on this page is about <strong>{scopeLine}</strong>. Switch
                            club or team in the bar above.
                        </p>
                    </div>
                    <div className="insightsStatStrip" aria-label="Match counts">
                        <div className="insightsStat">
                            <b>{loadingMatches ? '-' : totalItems}</b>
                            <i>Matches</i>
                        </div>
                        <div className="insightsStat">
                            <b>{loadingMatches ? '-' : matches.length}</b>
                            <i>This page</i>
                        </div>
                    </div>
                </div>

                {scope.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={scope.error}
                        style={{ marginBottom: 14 }}
                    />
                )}

                {!scope.loading && scope.selectedClubId == null && (
                    <Alert
                        type="info"
                        showIcon
                        message="No club to analyse yet"
                        description="Insights starts from a club. Ask Total Waterpolo to create your club, then reload."
                        style={{ marginBottom: 14 }}
                    />
                )}

                <InsightsClubSummaryPanel
                    summary={summary}
                    nextMatch={nextMatch}
                    loading={summaryLoading || scope.loading}
                    scopeLabel={scopeLine}
                />

                <InsightsLastMatchPanel
                    lastMatch={lastMatch}
                    teamIds={teamIds}
                    loadingSummary={summaryLoading || scope.loading}
                />

                <section className="insightsPanel">
                    <div className="insightsPanelHead">
                        <h2>Scoring leaders</h2>
                        <span>
                            {leadersMatches > 0 && leadersMatches < LEADERS_MATCHES
                                ? `${leadersMatches} of last ${LEADERS_MATCHES} available`
                                : `Last ${LEADERS_MATCHES} finished matches`}
                        </span>
                    </div>
                    <Spin spinning={leadersLoading}>
                        {leaders.length === 0 && !leadersLoading ? (
                            <div className="insightsEmpty">
                                No scoring events in the recent matches of this club.
                            </div>
                        ) : (
                            <ol className="insightsLeaders">
                                {leaders.slice(0, LEADERS_SHOWN).map((row: PlayerSeasonRow) => (
                                    <li key={row.playerId} className="insightsLeaderRow">
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            <span className="insightsLeaderName">{row.name}</span>
                                            <span className="insightsLeaderMeta">
                                                {row.shots} shots ·{' '}
                                                {shootingPercentText(row.goals, row.shots)} ·{' '}
                                                {row.assists} assists
                                            </span>
                                        </Link>
                                        <b>{row.goals}</b>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </Spin>
                    <p className="insightsTableNote">
                        <Link to={Routes.ROUTE_INSIGHTS_PLAYERS}>See all players</Link>
                    </p>
                </section>

                <section className="insightsPanel">
                    <div className="insightsPanelHead">
                        <h2>Match feed</h2>
                        <span>{clubName || 'Arena access rules apply'}</span>
                    </div>

                    <div className="insightsFilters">
                        <div className="insightsFilterField">
                            <label htmlFor="insights-filter-competition">Competition</label>
                            <Select
                                id="insights-filter-competition"
                                allowClear
                                showSearch
                                filterOption={filterOptionForSearch}
                                optionFilterProp="label"
                                placeholder="All competitions"
                                value={filters.competitionId}
                                options={competitionOptions}
                                onChange={(value: number | undefined) =>
                                    setFilters((prev) => ({ ...prev, competitionId: value }))
                                }
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="insightsFilterField">
                            <label htmlFor="insights-filter-opponent">Opponent</label>
                            <Select
                                id="insights-filter-opponent"
                                allowClear
                                showSearch
                                filterOption={filterOptionForSearch}
                                optionFilterProp="label"
                                placeholder="Any opponent"
                                value={filters.opponentTeamId}
                                options={opponentOptions}
                                onChange={(value: number | undefined) =>
                                    setFilters((prev) => ({ ...prev, opponentTeamId: value }))
                                }
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="insightsFilterField insightsFilterFieldDate">
                            <label htmlFor="insights-filter-date">From - to</label>
                            <DatePicker.RangePicker
                                id="insights-filter-date"
                                allowClear
                                value={filters.dateRange}
                                onChange={(dates) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        dateRange:
                                            dates && dates[0] && dates[1]
                                                ? [dates[0], dates[1]]
                                                : null,
                                    }))
                                }
                                style={{ width: '100%' }}
                                format="DD MMM YYYY"
                                placeholder={['From', 'To']}
                            />
                        </div>
                        <div className="insightsFilterActions">
                            <Button type="primary" size="small" onClick={applyFilters}>
                                Apply
                            </Button>
                            <Button
                                size="small"
                                onClick={clearFilters}
                                disabled={
                                    !hasActiveFilters(filters) && !hasActiveFilters(appliedFilters)
                                }
                            >
                                Clear
                            </Button>
                            {filtersDirty && (
                                <span className="insightsFilterHint">Not applied yet</span>
                            )}
                        </div>
                    </div>

                    {matchesError && (
                        <Alert
                            type="error"
                            showIcon
                            message={matchesError}
                            style={{ margin: '0 12px 12px' }}
                        />
                    )}

                    <Spin spinning={loadingMatches || scope.loading}>
                        {matches.length === 0 && !loadingMatches ? (
                            <div className="insightsEmpty">
                                {hasActiveFilters(appliedFilters) || selectedTeamId != null ? (
                                    <>No matches for this selection. Clear or widen the search.</>
                                ) : (
                                    <>
                                        No matches for {clubName || 'this club'} yet.{' '}
                                        <Link to={Routes.ROUTE_MATCHES}>Create one in Matches</Link>{' '}
                                        after teams and competition are set up.
                                    </>
                                )}
                            </div>
                        ) : (
                            <table className="insightsMatchTable">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Fixture</th>
                                        <th>Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {matches.map((match: MatchVm) => (
                                        <tr key={match.id}>
                                            <td>
                                                <Link
                                                    className="insightsMatchLink"
                                                    to={matchHref(match.id)}
                                                >
                                                    <div className="insightsDate">
                                                        {match.startDate
                                                            ? moment(match.startDate).format(
                                                                  'DD MMM YY HH:mm'
                                                              )
                                                            : '-'}
                                                    </div>
                                                </Link>
                                            </td>
                                            <td>
                                                <Link
                                                    className="insightsMatchLink"
                                                    to={matchHref(match.id)}
                                                >
                                                    <div className="insightsTeams">
                                                        {match.homeTeamDisplayName}
                                                        {' vs '}
                                                        {match.awayTeamDisplayName || 'TBD'}
                                                    </div>
                                                    <div className="insightsMeta">
                                                        {match.competitionDisplayName}
                                                        {match.round ? ` · ${match.round}` : ''}
                                                    </div>
                                                </Link>
                                            </td>
                                            <td>
                                                <Link
                                                    className="insightsMatchLink"
                                                    to={matchHref(match.id)}
                                                >
                                                    <div className="insightsScore">
                                                        {insightsScoreText(match)}
                                                    </div>
                                                    {match.status && (
                                                        <span
                                                            className={
                                                                insightsIsLiveStatus(match)
                                                                    ? 'insightsStatus isLive'
                                                                    : 'insightsStatus'
                                                            }
                                                        >
                                                            {match.status}
                                                        </span>
                                                    )}
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Spin>

                    {totalItems > PAGE_SIZE && (
                        <div className="insightsPagination">
                            <Pagination
                                size="small"
                                current={pageIndex}
                                pageSize={PAGE_SIZE}
                                total={totalItems}
                                showSizeChanger={false}
                                onChange={(page: number) => loadMatches(page)}
                            />
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Insights;
